import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./OrganizationContextMenu.css";
import addOrgIcon from "../../assets/addorg.svg";
import verticalDots from "../../assets/verticalDots.svg";
import OrganizationItemContextMenu from "./OrganizationItemContextMenu";
import EditOrganizationModal from "../EditOrganizationModal/EditOrganizationModal";
import { deleteOrganization } from "../../store/usersSlice";
import { useDispatch, useSelector } from "react-redux";
import { currentOrganizationSelector } from "../../store/selectors";
import {
  fetchProjects,
  resetProjectsExplorer,
} from "../../store/projectsSlice";
import { useNavigate } from "react-router";

const OrganizationContextMenu = ({
  organizations,
  onSelect,
  onClose,
  onAddOrganization,
  hideAddOrganization = false,
  menuWidth,
  menuRight,
  setOrganizationToRename,
  isProfile = false,
  isOpen,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [hoveredOrgId, setHoveredOrgId] = useState(null);
  const navigate = useNavigate();
  const [showOrgItemContextMenu, setShowOrgItemContextMenu] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const currentOrganization = useSelector(currentOrganizationSelector);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });

  const contextMenuRef = useRef(null);
  const orgItemContextMenuRef = useRef(null);
  const dotsRefs = useRef({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the organization item context menu
      if (showOrgItemContextMenu) {
        const isClickInsideMenu =
          orgItemContextMenuRef.current &&
          orgItemContextMenuRef.current.contains(event.target);

        // Check if click is on any dots icon
        const isClickOnDots = Object.values(dotsRefs.current).some(
          (ref) => ref && ref.contains(event.target)
        );

        if (!isClickInsideMenu && !isClickOnDots) {
          setShowOrgItemContextMenu(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOrgItemContextMenu]);

  // Add click outside handler for the main organization context menu
  useEffect(() => {
    const handleMainMenuClickOutside = (event) => {
      // Check if click is outside the main organization context menu
      const isClickInsideMainMenu =
        contextMenuRef.current && contextMenuRef.current.contains(event.target);

      // Check if click is on the dropdown trigger (this would be passed from parent)
      // For now, we'll close the menu if click is outside the menu itself
      if (!isClickInsideMainMenu) {
        onClose(event);
      }
    };

    document.addEventListener("mousedown", handleMainMenuClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleMainMenuClickOutside);
    };
  }, [onClose]);

  const handleOrgDotsClick = (org, event) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setContextMenuPosition({
      top: rect.bottom + window.scrollY - 40,
      left: rect.right + window.scrollX + 13,
    });
    setSelectedOrg(org);
    setShowOrgItemContextMenu(true);
  };

  const handleRename = (selectedOrg) => {
    setOrganizationToRename(selectedOrg);
  };

  const handleOrganizationChange = (organization) => {
    dispatch({ type: "SET_CURRENT_ORGANIZATION", payload: organization });
    dispatch(
      fetchProjects({
        organizationId: organization.id,
        isDeleted: false,
      })
    ).then((res) => {
      if (!res.error) {
        dispatch(resetProjectsExplorer());
        if (!isProfile) {
          navigate("/home");
        }
      }
    });
  };

  const handleDelete = (selectedOrg) => {
    if (organizations.length === 1) {
      alert("Вы не можете удалить последнюю организацию!");
    }
    if (window.confirm(t("projectScreen.confirmDeleteOrg"))) {
      dispatch(deleteOrganization(selectedOrg.id)).then((res) => {
        if (!res.error) {
          if (currentOrganization.id === selectedOrg.id) {
            handleOrganizationChange(organizations[0]);
          }
        }
      });
      setShowOrgItemContextMenu(false);
    }
  };

  const handleOrgItemContextMenuAction = (action) => {
    if (action === "rename") {
      handleRename(selectedOrg);
    } else if (action === "delete") {
      handleDelete(selectedOrg);
    }

    setShowOrgItemContextMenu(false);
  };

  const handleRenameAction = (e) => {
    handleOrgItemContextMenuAction("rename");
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="organization-context-menu"
        style={{
          ...(menuWidth ? { width: menuWidth } : {}),
          ...(menuRight !== undefined ? { right: menuRight } : {}),
        }}
        ref={contextMenuRef}
      >
        <div className="context-organization-list">
          {organizations.map((org) => (
            <div
              key={org.id}
              className="context-organization-item"
              onClick={(e) => {
                onSelect(org);
                onClose(e);
              }}
              onMouseEnter={() => setHoveredOrgId(org.id)}
              onMouseLeave={() => setHoveredOrgId(null)}
              style={{ position: "relative" }}
            >
              <span
                className="context-organization-name"
                style={{
                  marginRight: true ? "24px" : "0",
                  transition: "margin-right 0.1s ease",
                }}
              >
                {org.title}
              </span>
              {true && (
                <div
                  ref={(el) => {
                    if (el) {
                      dotsRefs.current[org.id] = el;
                    }
                  }}
                  onClick={(e) => handleOrgDotsClick(org, e)}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    padding: "4px",
                  }}
                >
                  <img
                    src={verticalDots}
                    alt="options"
                    style={{
                      width: 16,
                      height: 16,
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {!hideAddOrganization && (
          <div
            className="context-organization-add-item"
            onClick={(e) => {
              onAddOrganization();
              onClose(e);
            }}
          >
            <img
              src={addOrgIcon}
              style={{ width: 16, height: 16, marginRight: 10 }}
            />
            <span className="context-organization-add-text">
              Добавить организацию
            </span>
          </div>
        )}
        {showOrgItemContextMenu && selectedOrg && (
          <OrganizationItemContextMenu
            ref={orgItemContextMenuRef}
            organization={selectedOrg}
            onRename={handleRenameAction}
            onDelete={(e) => handleOrgItemContextMenuAction("delete")}
            onClose={() => setShowOrgItemContextMenu(false)}
            usePortal={false}
            style={{
              position: "fixed",
              top: contextMenuPosition.top,
              left: contextMenuPosition.left,
              zIndex: 10000,
            }}
          />
        )}
      </div>
    </>
  );
};

export default OrganizationContextMenu;
